'use client';

import React, { Suspense, use, useEffect, useRef, useState } from 'react';
import contentfulClient from '@/utils/contentful/client';
import Section from '@/components/ui/Section';
import BlogPreview from '@/components/ui/Blog/BlogPreview';
import { Entry, Tag } from 'contentful';
import {
  BlogPostEntry,
  BlogPostSkeleton,
  TagEntry,
  TagSkeleton
} from '@/utils/contentful/types';
import { Loader2Icon, SearchCheck } from 'lucide-react';
import Button from '@/components/ui/Button';
import ControlPanel from '@/components/ui/Blog/ControlPanel';
import Title from '@/components/ui/Title';
import Container from '@/components/ui/Container';
import { Dictionary, Locales } from '@/app/[lang]/dictonaries';

type BlogListProps = {
  lang: Locales;
  dict: Dictionary;
  options?: {
    loadPostsByTag?: string;
    showImportantPostsAlert?: boolean;
    controlPanel?: {
      showSearch?: boolean;
      showTagSearch?: boolean;
    };
  };
};

const PAGE_SIZE = 8; // Number of posts to fetch per request
const importantTagSlug = 'tarkeaa';

export default function BlogList({ lang, dict, options }: BlogListProps) {
  const client = contentfulClient();

  const [allPosts, setAllPosts] = useState<BlogPostEntry[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPostEntry[]>([]);
  const [posts, setPosts] = useState<BlogPostEntry[]>([]);
  const [tags, setTags] = useState<TagEntry[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagEntry[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showImportantPostsAlert, setShowImportantPostsAlert] = useState(
    options?.showImportantPostsAlert ?? true
  );
  const [searchQuery, setSearchQuery] = useState<string>('');

  const skipRef = useRef(0);

  // Fetches all blog posts from Contentful
  const fetchPosts = async () => {
    setLoading(true);

    const res = await client.getEntries<BlogPostSkeleton>({
      content_type: 'blog',
      order: ['-sys.createdAt'],
      locale: lang,
      include: 2
    });

    const currentDate = new Date();

    const filteredEntries = res.items.filter((item) => {
      let isValidDate = true;
      let hasTag = true;

      if (item.fields.expiryDate) {
        // No expiry date means it's always valid
        const expiryDate = new Date(item.fields.expiryDate);
        isValidDate = expiryDate >= currentDate;
      }

      if (options?.loadPostsByTag) {
        hasTag =
          item.fields.tags?.some((tag) => {
            const tagEntry = tag as TagEntry;
            return tagEntry.fields.slug === options.loadPostsByTag;
          }) ?? false;
      }

      return isValidDate && hasTag;
    });

    setAllPosts(filteredEntries);
    setLoading(false);
  };

  const fetchTags = async () => {
    const res = await client.getEntries<TagSkeleton>({
      content_type: 'tag',
      locale: lang
    });

    setTags(res.items);
  };

  // Loads more posts when the "Load More" button is clicked
  const loadMorePosts = () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setPosts(slicePosts(filteredPosts));
    setLoading(false);
  };

  // Slices the posts array to display a limited number of posts at a time
  const slicePosts = (posts: BlogPostEntry[]) => {
    const oldPostsLength = posts.length;
    const slicedPosts = posts.slice(0, skipRef.current + PAGE_SIZE);

    skipRef.current += PAGE_SIZE;

    if (slicedPosts.length - oldPostsLength === 0) {
      setHasMore(false);
    }

    return slicedPosts;
  };

  // Sorts posts by creation date in descending order
  const sortByCreatedAt = (
    a: Entry<BlogPostSkeleton>,
    b: Entry<BlogPostSkeleton>
  ) => {
    return (
      new Date(b.sys.createdAt).getTime() - new Date(a.sys.createdAt).getTime()
    );
  };

  // Filters posts by title based on the search query
  const filterPosts = (query: string, tags: TagEntry[]) => {
    if (!query && tags.length <= 0) return;

    console.log('Filtering posts with query:', query, 'and tags:', tags);

    //Filters posts by title based on the search query
    const filteredPosts = allPosts.filter((post) => {
      const title = post.fields.title?.toLowerCase().trim() || '';
      const matchesQuery = query
        ? title.includes(query.toLowerCase().trim())
        : true;

      const matchesTags =
        tags.length > 0
          ? (post.fields.tags?.some((tag) =>
              tags.some((t) => t.sys.id === tag.sys.id)
            ) ?? false)
          : true;

      return matchesQuery && matchesTags;
    });

    setFilteredPosts(filteredPosts.sort(sortByCreatedAt));
  };

  // Checks if there are any posts with the "important" tag
  const checkForImportantPosts = (posts: BlogPostEntry[]) => {
    return posts.some((post) =>
      post.fields.tags?.some((tag) => {
        const tagEntry = tag as TagEntry;
        return tagEntry.fields.slug === importantTagSlug;
      })
    );
  };

  // Shows only posts with the "important" tag when the button is clicked
  const showImportantPosts = () => {
    if (!options?.showImportantPostsAlert) {
      return;
    }

    setSearchQuery('');
    setSelectedTags((prev) => {
      const importantTag = tags.find(
        (tag) => tag.fields.slug === importantTagSlug
      );

      if (importantTag) {
        return [importantTag];
      }

      return prev;
    });
  };

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, []);

  useEffect(() => {
    skipRef.current = 0; // Reset skip counter when search query changes
    setHasMore(true); // Reset hasMore when search query changes

    if (searchQuery || selectedTags.length > 0) {
      filterPosts(searchQuery, selectedTags);
    } else {
      // If search query is empty, reset filtered posts to all posts
      setFilteredPosts(allPosts);
    }
  }, [searchQuery, selectedTags]);

  useEffect(() => {
    skipRef.current = 0; // Reset skip counter when allPosts changes

    setSearchQuery(''); // Reset search query when allPosts changes
    setHasMore(true); // Reset hasMore when allPosts changes
    setFilteredPosts(allPosts); // Reset filteredPosts to allPosts
  }, [allPosts]);

  useEffect(() => {
    if (filteredPosts.length > 0) {
      // If there are filtered posts, slice them to display
      setPosts(slicePosts(filteredPosts));
    } else {
      // If no filtered posts, reset posts to empty
      setPosts([]);
    }
  }, [filteredPosts]);

  return (
    <Section className="flex flex-col gap-8">
      <ControlPanel
        setSearchQuery={setSearchQuery}
        searchQuery={searchQuery}
        setSelectedTags={setSelectedTags}
        selectedTags={selectedTags}
        options={options?.controlPanel}
        tags={tags}
        dict={dict}
      />
      {showImportantPostsAlert && checkForImportantPosts(filteredPosts) && (
        <Container>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between bg-yellow-100 text-yellow-800 p-4 rounded-lg">
            <span>{dict.pages.blog.important_post_alert.title}</span>

            <div className="flex items-center gap-4">
              <Button
                className="flex-1 bg-yellow-300 text-200 hover:bg-yellow-600"
                onClick={showImportantPosts}
              >
                {dict.pages.blog.important_post_alert.accept}
              </Button>
              <Button
                className="flex-1 bg-yellow-200 text-200 hover:bg-yellow-600"
                onClick={() => {
                  setShowImportantPostsAlert(false);
                  setSelectedTags([]);
                }}
              >
                {dict.pages.blog.important_post_alert.decline}
              </Button>
            </div>
          </div>
        </Container>
      )}
      <Container>
        <div className="flex items-center gap-2">
          <SearchCheck />
          <p>
            {filteredPosts.length >= 0 && filteredPosts.length !== 1
              ? dict.pages.blog.posts_found.many.replace(
                  '{{count}}',
                  filteredPosts.length.toString()
                )
              : dict.pages.blog.posts_found.one}
          </p>
        </div>
      </Container>
      <Suspense
        fallback={<Loader2Icon className="animate-spin text-white text-4xl" />}
      >
        {posts && posts.length > 0 ? (
          posts.map((post) => (
            <BlogPreview
              key={post.sys.id}
              post={post as BlogPostEntry}
              lang={lang}
              dict={dict}
            />
          ))
        ) : (
          <Container className="h-[350px] flex items-center justify-center">
            <Title className="text-center text-2xl lg:text-3xl">
              {dict.pages.blog.post.no_posts}
            </Title>
          </Container>
        )}
        {hasMore && posts.length > 0 && (
          <div className="mt-6 text-center">
            <Button
              variant={'slim'}
              onClick={() => loadMorePosts()}
              disabled={loading}
            >
              {loading
                ? dict.pages.blog.post.loading
                : dict.pages.blog.post.load_more}
            </Button>
          </div>
        )}
      </Suspense>
    </Section>
  );
}
